<div class="edit-page">
  <!-- Page Header -->
  <div class="edit-header">
    <div class="edit-header-left">
      <button class="back-btn" (click)="goBack()" aria-label="Go back">
        <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path
            d="M19 12H5M12 19l-7-7 7-7"
            stroke="currentColor"
            stroke-width="2"
            stroke-linecap="round"
            stroke-linejoin="round"
          />
        </svg>
      </button>
      <div class="edit-header-titles">
        <h1 class="edit-title">Business Information</h1>
        <p class="edit-subtitle">Update your business profile details</p>
      </div>
    </div>
  </div>

  @if (isLoading) {
    <div class="loading-state">
      <div class="loading-spinner"></div>
      <p>Loading your business information…</p>
    </div>
  } @else {
    <div class="edit-content">
      <!-- ── Personal Details ──────────────────────────────────── -->
      <div class="form-section">
        <h3 class="section-title">Personal Details</h3>
        <div class="form-grid">
          <div class="form-field">
            <label class="field-label" for="name">
              <span class="required">*</span>Name
            </label>
            <input
              id="name"
              type="text"
              class="field-input"
              [(ngModel)]="form.name"
              name="name"
              placeholder="Enter your name"
            />
            @if (submitted && validationErrors["name"]) {
              <span class="field-hint error">{{
                validationErrors["name"]
              }}</span>
            }
          </div>

          <div class="form-field">
            <label class="field-label" for="email">
              <span class="required">*</span>Email
            </label>
            <input
              id="email"
              type="email"
              class="field-input"
              [(ngModel)]="form.email"
              name="email"
              placeholder="Enter your email address"
            />
            @if (submitted && validationErrors["email"]) {
              <span class="field-hint error">{{
                validationErrors["email"]
              }}</span>
            }
          </div>

          <div class="form-field">
            <label class="field-label" for="phone">Phone</label>
            <input
              id="phone"
              type="tel"
              class="field-input"
              [(ngModel)]="form.phone"
              name="phone"
              placeholder="Enter your phone number"
            />
            @if (submitted && validationErrors["phone"]) {
              <span class="field-hint error">{{
                validationErrors["phone"]
              }}</span>
            }
          </div>

          <div class="form-field">
            <label class="field-label" for="preferred-language">
              <span class="required">*</span>Preferred Language
            </label>
            <select
              id="preferred-language"
              class="field-select"
              [(ngModel)]="form.preferredLanguage"
              name="preferredLanguage"
            >
              <option value="">Select language</option>
              @for (lang of languages; track lang.value) {
                <option [value]="lang.value">{{ lang.label }}</option>
              }
            </select>
            @if (submitted && validationErrors["preferredLanguage"]) {
              <span class="field-hint error">{{
                validationErrors["preferredLanguage"]
              }}</span>
            }
          </div>
        </div>
      </div>

      <!-- ── Do you have a business? ────────────────────────────── -->
      <div class="form-section">
        <h3 class="section-title">Business Status</h3>
        <p class="section-subtitle">Do you currently have a business?</p>
        <div class="radio-cards">
          <button
            class="radio-card"
            [class.selected]="form.hasBusiness === 'yes'"
            (click)="form.hasBusiness = 'yes'; onHasBusinessChange()"
            type="button"
            id="has-business-yes"
          >
            <div
              class="radio-dot"
              [class.active]="form.hasBusiness === 'yes'"
            ></div>
            <div class="radio-text">
              <span class="radio-label">Yes, I have a business</span>
              <span class="radio-desc">I currently operate a business</span>
            </div>
          </button>
          <button
            class="radio-card"
            [class.selected]="form.hasBusiness === 'no'"
            (click)="form.hasBusiness = 'no'; onHasBusinessChange()"
            type="button"
            id="has-business-no"
          >
            <div
              class="radio-dot"
              [class.active]="form.hasBusiness === 'no'"
            ></div>
            <div class="radio-text">
              <span class="radio-label">No business yet</span>
              <span class="radio-desc">I do not have a business</span>
            </div>
          </button>
          <button
            class="radio-card"
            [class.selected]="form.hasBusiness === 'planning'"
            (click)="form.hasBusiness = 'planning'; onHasBusinessChange()"
            type="button"
            id="has-business-planning"
          >
            <div
              class="radio-dot"
              [class.active]="form.hasBusiness === 'planning'"
            ></div>
            <div class="radio-text">
              <span class="radio-label">Planning to start</span>
              <span class="radio-desc">I'm planning to start a business</span>
            </div>
          </button>
        </div>
        @if (submitted && validationErrors["hasBusiness"]) {
          <span class="field-hint error" style="margin-top: 6px">{{
            validationErrors["hasBusiness"]
          }}</span>
        }
      </div>

      <!-- ── Business Details (only when hasBusiness === 'yes') ── -->
      @if (form.hasBusiness === "yes") {
        <div class="form-section">
          <h3 class="section-title">Business Details</h3>
          <div class="form-grid">
            <div class="form-field">
              <label class="field-label" for="business-name"
                ><span class="required">*</span>Business / Company Name</label
              >
              <input
                id="business-name"
                type="text"
                class="field-input"
                [(ngModel)]="form.businessName"
                name="businessName"
                placeholder="e.g. Acme Corp"
              />
              @if (submitted && validationErrors["businessName"]) {
                <span class="field-hint error">{{
                  validationErrors["businessName"]
                }}</span>
              }
            </div>
            <div class="form-field">
              <label class="field-label" for="business-website"
                >Business Website</label
              >
              <input
                id="business-website"
                type="url"
                class="field-input"
                [(ngModel)]="form.businessWebsite"
                name="businessWebsite"
                placeholder="https://example.com"
              />
              @if (submitted && validationErrors["businessWebsite"]) {
                <span class="field-hint error">{{
                  validationErrors["businessWebsite"]
                }}</span>
              }
            </div>
            <div class="form-field">
              <label class="field-label" for="business-type"
                ><span class="required">*</span>Business Type</label
              >
              <select
                id="business-type"
                class="field-input field-select"
                [(ngModel)]="form.businessType"
                name="businessType"
              >
                <option value="">Select a business type</option>
                @for (option of businessTypeOptions; track option.value) {
                  <option [value]="option.value">{{ option.label }}</option>
                }
              </select>
              @if (submitted && validationErrors["businessType"]) {
                <span class="field-hint error">{{
                  validationErrors["businessType"]
                }}</span>
              }
            </div>
            <div class="form-field">
              <label class="field-label" for="business-established">
                <span class="required">*</span>Year / Date Established
              </label>
              <input
                id="business-established"
                type="text"
                class="field-input"
                [(ngModel)]="form.businessEstablished"
                name="businessEstablished"
                placeholder="e.g. 2005 or MM/DD/YYYY"
              />
              @if (submitted && validationErrors["businessEstablished"]) {
                <span class="field-hint error">{{
                  validationErrors["businessEstablished"]
                }}</span>
              }
            </div>
            <div class="form-field">
              <label class="field-label" for="num-employees"
                ><span class="required">*</span>Number of Employees</label
              >
              <input
                id="num-employees"
                type="number"
                class="field-input"
                [(ngModel)]="form.numberOfEmployees"
                name="numberOfEmployees"
                min="0"
                placeholder="e.g. 10"
                (keydown)="blockDecimalAndSigns($event)"
                (paste)="blockPasteDecimal($event)"
              />
              @if (submitted && validationErrors["numberOfEmployees"]) {
                <span class="field-hint error">{{
                  validationErrors["numberOfEmployees"]
                }}</span>
              }
            </div>
          </div>
        </div>
      }

      <!-- ── Application Summary ──────────────────────────────── -->
      <div class="form-section">
        <h3 class="section-title">
          @if (form.hasBusiness === "yes") {
            Tell us about your business
          } @else {
            Tell us about yourself
          }
        </h3>
        <div class="form-field full-width">
          <label class="field-label" for="application-summary">
            <span class="required">*</span>Application Summary
          </label>
          <textarea
            id="application-summary"
            class="field-textarea"
            [(ngModel)]="form.applicationSummary"
            name="applicationSummary"
            rows="4"
            placeholder="Briefly describe your business or yourself..."
          ></textarea>
          @if (submitted && validationErrors["applicationSummary"]) {
            <span class="field-hint error">{{
              validationErrors["applicationSummary"]
            }}</span>
          }
        </div>
      </div>

      <!-- ── Location ────────────────────────────────────────── -->
      <div class="form-section">
        <h3 class="section-title">
          @if (form.hasBusiness === "yes") {
            Business Location
          } @else {
            Your Location
          }
        </h3>
        <div class="form-field full-width">
          <label class="field-label" for="business-address">
            <span class="required">*</span>
            @if (form.hasBusiness === "yes") {
              Business Address
            } @else {
              Address
            }
          </label>
          <input
            id="business-address"
            type="text"
            class="field-input"
            [(ngModel)]="form.businessAddress"
            name="businessAddress"
            placeholder="Street address"
          />
          @if (submitted && validationErrors["businessAddress"]) {
            <span class="field-hint error">{{
              validationErrors["businessAddress"]
            }}</span>
          }
        </div>
        <div class="form-grid">
          <div class="form-field">
            <label class="field-label" for="state-select">
              <span class="required">*</span>State
            </label>
            <app-combobox
              id="state-select"
              name="state-select"
              [(ngModel)]="form.state"
              [required]="true"
              (ngModelChange)="onStateChange()"
              [options]="states"
              labelKey="name"
              valueKey="name"
              [placeholder]="getStatePlaceholder()"
              [disabled]="isLoadingStates"
              [autoSort]="true"
            ></app-combobox>
            @if (
              submitted &&
              (validationErrors["state"] || validationErrors["stateId"])
            ) {
              <span class="field-hint error">{{
                validationErrors["state"] || validationErrors["stateId"]
              }}</span>
            }
          </div>
          <div class="form-field">
            <label class="field-label" for="county-select">
              <span class="required">*</span>County
            </label>
            <app-combobox
              id="county-select"
              name="county-select"
              [(ngModel)]="form.county"
              [required]="true"
              (ngModelChange)="onCountyChange()"
              [options]="counties"
              labelKey="name"
              valueKey="name"
              [placeholder]="getCountyPlaceholder()"
              [disabled]="
                !form.state || isLoadingCounties || counties.length === 0
              "
              [autoSort]="true"
            ></app-combobox>
            @if (
              submitted &&
              (validationErrors["county"] || validationErrors["countyId"])
            ) {
              <span class="field-hint error">{{
                validationErrors["county"] || validationErrors["countyId"]
              }}</span>
            }
          </div>
          <div class="form-field">
            <label class="field-label" for="city-select">
              <span class="required">*</span>City
            </label>
            <app-combobox
              id="city-select"
              name="city-select"
              [(ngModel)]="form.city"
              [required]="true"
              (ngModelChange)="onCityChange()"
              [options]="cities"
              labelKey="name"
              valueKey="name"
              [labelFormatter]="formatCityItem"
              [placeholder]="getCityPlaceholder()"
              [disabled]="
                !form.county || isLoadingCounties || cities.length === 0
              "
              [autoSort]="true"
            ></app-combobox>
            @if (
              submitted &&
              (validationErrors["city"] || validationErrors["cityId"])
            ) {
              <span class="field-hint error">{{
                validationErrors["city"] || validationErrors["cityId"]
              }}</span>
            }
          </div>
          <div class="form-field">
            <label class="field-label" for="zip-select">
              <span class="required">*</span>Zip Code
            </label>
            <app-combobox
              id="zip-select"
              name="zip-select"
              [(ngModel)]="form.zipCode"
              [required]="true"
              [options]="zipcodes"
              labelKey="code"
              valueKey="code"
              [placeholder]="getZipPlaceholder()"
              [disabled]="
                !form.city || isLoadingZipcodes || zipcodes.length === 0
              "
              [autoSort]="true"
            ></app-combobox>
            @if (submitted && validationErrors["zipCode"]) {
              <span class="field-hint error">{{
                validationErrors["zipCode"]
              }}</span>
            }
          </div>
        </div>
      </div>

      <!-- ── Additional Details ──────────────────────────────── -->
      <div class="form-section">
        <h3 class="section-title">Additional Details</h3>
        <div class="form-field-group">
          <p class="field-label group-label">
            <span class="required">*</span>Are you a US Military Veteran?
          </p>
          <div class="yes-no-row">
            <button
              class="yn-btn"
              [class.selected]="form.isMilitaryVeteran === true"
              (click)="form.isMilitaryVeteran = true"
              type="button"
              id="veteran-yes"
            >
              Yes
            </button>
            <button
              class="yn-btn"
              [class.selected]="form.isMilitaryVeteran === false"
              (click)="form.isMilitaryVeteran = false"
              type="button"
              id="veteran-no"
            >
              No
            </button>
          </div>
          @if (submitted && validationErrors["isMilitaryVeteran"]) {
            <span class="field-hint error" style="margin-top: 6px">{{
              validationErrors["isMilitaryVeteran"]
            }}</span>
          }
        </div>
        <div class="form-field-group">
          <p class="field-label group-label">
            <span class="required">*</span>Are you a spouse of a US Military
            Veteran?
          </p>
          <div class="yes-no-row">
            <button
              class="yn-btn"
              [class.selected]="form.isVeteranSpouse === true"
              (click)="form.isVeteranSpouse = true"
              type="button"
              id="veteran-spouse-yes"
            >
              Yes
            </button>
            <button
              class="yn-btn"
              [class.selected]="form.isVeteranSpouse === false"
              (click)="form.isVeteranSpouse = false"
              type="button"
              id="veteran-spouse-no"
            >
              No
            </button>
          </div>
          @if (submitted && validationErrors["isVeteranSpouse"]) {
            <span class="field-hint error">{{
              validationErrors["isVeteranSpouse"]
            }}</span>
          }
        </div>
      </div>

      <!-- ── Form Actions at the Bottom ── -->
      <div class="actions-container">
        <div class="warning-notice-box">
          <svg
            class="warning-notice-icon"
            viewBox="0 0 24 24"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M12 9v4m0 4h.01M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z"
              stroke="currentColor"
              stroke-width="2"
              stroke-linecap="round"
              stroke-linejoin="round"
            />
          </svg>
          <div class="warning-notice-content">
            <p class="warning-notice-title">Organization Rematching Notice</p>
            <p class="warning-notice-desc">
              Updating your business details will trigger a new organization
              matching process. Any previous match requests may be overwritten
              based on your updated business information.
            </p>
          </div>
        </div>

        @if (submitted && hasErrors) {
          <div class="error-summary-box">
            <svg
              class="error-summary-icon"
              viewBox="0 0 24 24"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M12 22c5.523 0 10-4.477 10-10S17.523 2 12 2 2 6.477 2 12s4.477 10 10 10z"
                stroke="#dc2626"
                stroke-width="2"
              />
              <path
                d="M12 8v4"
                stroke="#dc2626"
                stroke-width="2"
                stroke-linecap="round"
              />
              <circle cx="12" cy="16" r="1.25" fill="#dc2626" />
            </svg>
            <div class="error-summary-content">
              <p class="error-summary-title">
                Please correct the highlighted errors before saving.
              </p>
            </div>
          </div>
        }

        <div class="form-actions">
          <button
            class="cancel-btn"
            (click)="goBack()"
            [disabled]="isSaving"
            type="button"
          >
            Cancel
          </button>
          <button
            class="save-btn"
            [disabled]="isSaving"
            (click)="save()"
            id="save-business-info-btn"
            type="button"
          >
            @if (isSaving) {
              <span class="spinner"></span>
              Saving…
            } @else {
              <svg
                viewBox="0 0 24 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M5 13l4 4L19 7"
                  stroke="currentColor"
                  stroke-width="2"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                />
              </svg>
              Save
            }
          </button>
        </div>
      </div>
    </div>
  }
</div>
